import { useMutation } from '@tanstack/react-query';
import { CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { billingApi } from '@/lib/billing-api';
import { getErrorMessage } from '@/lib/errors';

/**
 * Sends the customer to the payment provider to manage their card.
 *
 * ## Why there is no card form here
 *
 * Card details never touch this platform, and that is the single biggest reason its exposure to
 * card data is nil. Building an "add or remove a payment method" screen — even one where the
 * provider's script collects the number — means owning 3DS challenges, SCA in Europe, regional
 * payment methods, retry behaviour, and every change the card schemes push down. The provider
 * already hosts all of that and keeps it current.
 *
 * So this is one button. It costs nothing to maintain and takes on none of that risk.
 *
 * ## Why it exists at all
 *
 * Cards expire. When one does at renewal the payment fails, the subscription goes past due and the
 * account falls past due — and with no way to update the card, a customer who wanted to keep paying
 * has to contact support or leave. That is churn among people who were perfectly happy.
 *
 * ## The link is fetched on click, never with the page
 *
 * It carries an authentication token and is short-lived. Loading it alongside the page would put a
 * working key to somebody's billing details into a query cache, a browser history entry, and any
 * log that records it.
 */
export function PaymentMethodCard() {
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: billingApi.paymentPortal,
    onSuccess: (link) => {
      setError(null);
      // A new tab, so the customer's place on the billing page survives the detour.
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (err: unknown) => setError(getErrorMessage(err)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-5" />
          Payment method
        </CardTitle>
        <CardDescription>
          Add, replace or remove the card your subscription is billed to. This opens your payment
          provider, who holds the card. It is never stored on this platform.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error !== null && <Alert variant="danger">{error}</Alert>}

        <Button onClick={() => open.mutate()} disabled={open.isPending}>
          {open.isPending ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <ExternalLink className="mr-2 size-4" />
          )}
          Manage payment method
        </Button>

        <p className="text-muted-foreground text-xs">
          Worth keeping current: if a card expires before a renewal, the payment fails. Access
          continues for 7 days while the payment is retried; if it is still unpaid then, the
          subscription is cancelled.
        </p>
      </CardContent>
    </Card>
  );
}
